<template>
  <div class="personal-counter-content">
    <p
      class="personal-counter-content__notify"
      data-test-id="personal-counter-content-notify"
    >
      {{ title }}
    </p>

    <p
      class="personal-counter-content__body"
      data-test-id="personal-counter-content-body"
    >
      <span v-safe-html="bodyText" />
      <ELink
        v-if="!loginFailed"
        color="secondary"
        :href="learnMoreLink"
        :data-track-id="trackIds.learnMoreLink"
        target="_blank"
        rel="noreferrer"
        data-test-id="personal-counter-content-more"
        @click="trackLearnMore"
      >
        {{ $t('common.generic.learnmore') }}
        <span class="sr-only">
          {{ $t('common.header.counter.sync') }}
        </span>
      </ELink>
    </p>

    <div
      class="personal-counter-content__actions"
    >
      <ELink
        v-if="!loggedIn"
        href="/account/login"
        data-test-id="personal-counter-content-signin"
        data-track-id="personal-counter-content-signin"
        rel="noopener"
        @click="trackLogin"
      >
        {{ $t('common.header.counter.loggedout.cta') }}
      </ELink>
      <ELink
        v-else
        as="span"
        data-test-id="personal-counter-content-signout"
        data-track-id="personal-counter-content-signout"
        @click="signOutUser"
      >
        {{ $t('common.header.counter.loggedin.cta') }}
      </ELink>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import EcosiaUser from '@ecosia/ecosia-user';

import { sendCoreAnalyticsEvent } from '~~/common/js/client/analytics';
import { getCookieOptions } from '~~/common/js/universal/utils';
import {
  MAIN as MAIN_COOKIE,
  HEIMDALL as HEIMDALL_COOKIE,
} from '~~/common/js/constants/cookies';
import { HELPSCOUT_LEARN_MORE_SYNC_COUNTER } from '~~/common/js/constants/urls';
import ELink from '~~/common/vue/components/link';

// TODO: We need to re-think how we want to implement component specific tracking
// adding TRACK_IDs to the component directly makes it difficult to re-use the components.
// It also should not be the concern of the component whats beeing tracked
const trackIds = {
  loggedIn: {
    learnMoreLink: 'personal-counter-content-learn-more--loggedin',
    feedbackLink: 'personal-counter-content-feedback--loggedin',
  },
  loggedOut: {
    learnMoreLink: 'personal-counter-content-learn-more--loggedout',
    feedbackLink: 'personal-counter-content-feedback--loggedout',
  },
};

export default {
  name: 'EPersonalCounterContent',
  components: {
    ELink,
  },
  computed: {
    ...mapState('user', [
      'loggedIn',
      'loginFailed',
    ]),
    ...mapState('global', ['locale']),
    title() {
      if (this.loginFailed) {
        return this.$t('common.generic.error');
      }

      if (this.loggedIn) {
        return this.$t('common.header.counter.loggedin.title');
      }

      return null;
    },
    bodyText() {
      if (this.loginFailed) {
        return this.$t('common.generic.tryagain');
      }

      const translatedString = `common.header.counter.logged${this.loggedIn ? 'in' : 'out'}.body`;
      return this.$t(translatedString);
    },
    learnMoreLink() {
      return HELPSCOUT_LEARN_MORE_SYNC_COUNTER;
    },
    trackIds() {
      return this.loggedIn ? trackIds.loggedIn : trackIds.loggedOut;
    },
  },
  methods: {
    signOutUser() {
      // Set the cookie to the hostname, plus a leading dot, minus any port
      // eslint-disable-next-line compat/compat
      const domain = `.${window.location.hostname.split(':')[0]}`;
      const options = getCookieOptions({ domain });
      const cookieString = this.$cookieManager.get(MAIN_COOKIE);
      const user = new EcosiaUser({ cookieString });

      this.$cookieManager.remove(HEIMDALL_COOKIE, options);
      user.set('treeCount', 0);
      window.location.reload(true);
    },
    trackLogin() {
      sendCoreAnalyticsEvent('personalCounterClickLogin');
    },
    trackLearnMore() {
      sendCoreAnalyticsEvent('personalCounterClickLearnMore', { loggedIn: this.loggedIn });
    },
  },
};
</script>

<style lang="scss" scoped>

.personal-counter-content {
  position: relative;
  background-color: var(--color-background-elevation-1);

  &__body {
    margin: 0;
    font-size: $font-m;
    line-height: $line-height-m;
    padding: $space-s $space-m 0;
    border-bottom: $border-width;
    color: var(--color-text-primary);
  }

  &__notify {
    margin: 0;
    font-size: $font-m;
    font-weight: $font-weight-1l;
    line-height: $line-height-m;
    padding: $space-m $space-m 0;

    & + .personal-counter-content__body {
      padding-top: $space-1s;
    }
  }

  &__actions {
    @include divider('top');

    font-size: $font-m;
    padding: $space-s $space-m;
    margin-top: $space-s;
  }
}
</style>

<docs>
```js
<EPersonalCounterContent />
```
</docs>
